import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { DatabaseIcon, ShieldCheckIcon, ClipboardListIcon, CheckCircleIcon, CalendarIcon, TargetIcon, FileTextIcon, BrainCircuitIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
function AnimatedCounter({
  from,
  to,
  duration = 2,
  suffix = ''
}: {
  from: number;
  to: number;
  duration?: number;
  suffix?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => Math.round(latest) + suffix);
  useEffect(() => {
    if (inView) {
      animate(count, to, {
        duration
      });
    }
  }, [count, inView, to, duration]);
  return <motion.span ref={ref} data-id="element-1274" data-mp="2ap4yyynj258a">{rounded}</motion.span>;
}
export function HomePage() {
  return <div className="w-full" data-id="element-1275" data-mp="2eqgo732hoofy">
      {/* SECTION 1: HERO */}
      <section className="section-navy hero-grid-pattern py-28 md:py-28 sm:py-16" data-id="element-1276" data-mp="2eox574pg7zah">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1277" data-mp="2jihxqh89w12a">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-1278" data-mp="2ogt60nix63ie">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight text-white tracking-wide" data-id="element-1279" data-mp="1rq0r3t7q2hi7">
              Trusted ServiceNow, ICAM, and Program Management Partner for
              Federal Agencies and Enterprises
            </h1>
            <div className="w-[120px] h-1 bg-nrs-orange mb-6 rounded-full" data-id="element-1280" data-mp="20pnvmnvr03fh"></div>
            <p className="text-xl text-white mb-8 max-w-3xl" data-id="element-1281" data-mp="25q22i2lm57bx">
              Expert delivery. Cleared teams. Mission-critical results.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-1282" data-mp="3c7ynf74id3ut">
              <Link to="/services" className="btn-primary" data-id="element-1283" data-mp="3h3noxg038utr">
                Explore Our Services
              </Link>
              <Link to="/about-us/contract-vehicles" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-1284" data-mp="2reyrh1sauq1y">
                View Contract Vehicles
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 items-start sm:items-center" data-id="element-1285" data-mp="31cmyor73dq10">
              <div className="flex items-center gap-2" data-id="element-1286" data-mp="2we1wwnc6yyag">
                <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0" data-id="element-1287" data-mp="28rd7zhorm5kc" />
                <span className="text-white text-base" data-id="element-1288" data-mp="23s0eie3e208s">
                  19 Years Federal Experience
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1289" data-mp="2ii7yiy91nrbl">
                <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0" data-id="element-1290" data-mp="2dp55cr86ijvx" />
                <span className="text-white text-base" data-id="element-1291" data-mp="1pzaozzonid6x">
                  Trusted Partner Across Federal Government and Enterprises
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1292" data-mp="1kywpj6148bki">
                <CheckCircleIcon className="h-5 w-5 text-nrs-orange flex-shrink-0" data-id="element-1293" data-mp="hx3iuek8v0y" />
                <span className="text-white text-base" data-id="element-1294" data-mp="5agq2gv870hw">
                  100% Cleared Workforce
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: FOUR CORE SERVICES */}
      <section className="section-white" data-id="element-1295" data-mp="3rmncd7kqaoxl">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1296" data-mp="j8wt9xtvq8va">
          <h2 className="text-3xl font-bold text-center mb-4 text-nrs-navy" data-id="element-1297" data-mp="o815yyg0ioqd">
            Our Core Services
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-1298" data-mp="a99t1fcvz93t"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1299" data-mp="e9kappa1jnyi">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} className="card flex flex-col items-center text-center h-full" data-id="element-1300" data-mp="ec6eddtt9jbg">
              <DatabaseIcon className="h-12 w-12 text-nrs-orange mb-6" data-id="element-1301" data-mp="2yy820ycjkusl" />
              <h3 className="text-2xl font-bold mb-4 text-nrs-navy" data-id="element-1302" data-mp="3f88qce1fc43g">
                ServiceNow Governance & Development
              </h3>
              <p className="text-gray-600 mb-8 flex-grow leading-relaxed" data-id="element-1303" data-mp="3aexm8knzy1f8">
                Complete platform stewardship from strategy to execution.
                Self-governing development model eliminates vendor conflicts.
                Expert delivery in secure environments for Defense and federal
                agency clients.
              </p>
              <Link to="/services/servicenow" className="btn-secondary w-full" data-id="element-1304" data-mp="3t8jkpr1y00td">
                Learn More
              </Link>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: 0.1
          }} className="card flex flex-col items-center text-center h-full" data-id="element-1305" data-mp="3k6tuupnpl9xp">
              <ShieldCheckIcon className="h-12 w-12 text-nrs-orange mb-6" data-id="element-1306" data-mp="28qlpkggw7kic" />
              <h3 className="text-2xl font-bold mb-4 text-nrs-navy" data-id="element-1307" data-mp="oa5gt891m46h">
                ICAM & Zero Trust Solutions
              </h3>
              <p className="text-gray-600 mb-8 flex-grow leading-relaxed" data-id="element-1308" data-mp="sbh77fzqqtfq">
                Federal identity and access management expertise for DoD and IC
                agencies. Zero Trust Architecture implementation, PIV/CAC
                integration, Single Sign-On, and privileged access management
                aligned with FICAM and NIST standards.
              </p>
              <Link to="/services/identity-access-management" className="btn-secondary w-full" data-id="element-1309" data-mp="1bxvshfmul7u5">
                Learn More
              </Link>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: 0.2
          }} className="card flex flex-col items-center text-center h-full" data-id="element-1310" data-mp="1kzd2cg9nwdae">
              <ClipboardListIcon className="h-12 w-12 text-nrs-orange mb-6" data-id="element-1311" data-mp="3mri2ug588iwq" />
              <h3 className="text-2xl font-bold mb-4 text-nrs-navy" data-id="element-1312" data-mp="txj7e480e85b">
                Program Management & PMO Services
              </h3>
              <p className="text-gray-600 mb-8 flex-grow leading-relaxed" data-id="element-1313" data-mp="oz9e5z5uhhmc">
                Mission-critical program delivery in secure environments. Agile
                and traditional program management, PMO establishment and
                operations. Cleared program managers, Scrum Masters, and PMO
                specialists.
              </p>
              <Link to="/services/program-and-project-management" className="btn-secondary w-full" data-id="element-1314" data-mp="17x2fix0tljhm">
                Learn More
              </Link>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: 0.3
          }} className="card flex flex-col items-center text-center h-full" data-id="element-1315" data-mp="1hm2dqe7i0aur">
              <BrainCircuitIcon className="h-12 w-12 text-nrs-orange mb-6" data-id="element-1316" data-mp="1qo3azb2qewwf" />
              <h3 className="text-2xl font-bold mb-4 text-nrs-navy" data-id="element-1317" data-mp="2a8akz7qnfxcx">
                AI & Intelligent Automation
              </h3>
              <p className="text-gray-600 mb-8 flex-grow leading-relaxed" data-id="element-1318" data-mp="2f751u4523bue">
                AI and intelligent automation solutions for federal agencies and
                enterprises. Implement generative AI, virtual agents, and
                agentic workflows primarily on ServiceNow platform. Enable
                natural language interactions, predictive intelligence, and
                autonomous task execution across ITSM, HRSD, and enterprise
                workflows.
              </p>
              <Link to="/services/ai" className="btn-secondary w-full" data-id="element-1319" data-mp="1hlg3mjp4pe3o">
                Learn More
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SECTION 3: WHY FEDERAL AGENCIES CHOOSE NRS */}
      <section className="section-light-blue" data-id="element-1320" data-mp="1ckk0w9gijxzu">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1321" data-mp="1cka86e433cqg">
          <h2 className="text-3xl font-bold text-center mb-4 text-nrs-navy" data-id="element-1322" data-mp="1t0kswfacwzrx">
            Why Federal Agencies and Enterprises Choose New River Systems
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-1323" data-mp="1j24zg5nuxef9"></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1324" data-mp="1j47xncz5ydv2">
            <div className="flex flex-col items-center text-center" data-id="element-1325" data-mp="1e2sczm0we9d7">
              <ShieldCheckIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1326" data-mp="1a2i7sbkxjmze" />
              <h3 className="text-xl font-bold mb-3 text-nrs-navy" data-id="element-1327" data-mp="15b03penm16a7">
                Cleared Teams
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1328" data-mp="10aucu0p5znt6">
                Cleared workforce ready to operate in the most sensitive secure
                environments federal agencies and enterprises require.
              </p>
            </div>

            <div className="flex flex-col items-center text-center" data-id="element-1329" data-mp="qfahbfeiir4y">
              <CalendarIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1330" data-mp="mce649raxxb1" />
              <h3 className="text-xl font-bold mb-3 text-nrs-navy" data-id="element-1331" data-mp="2xzzf61jqwc2u">
                19 Years Federal Experience
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1332" data-mp="31z8c1fa30q2m">
                Serving Department of Defense and federal agencies since 2007
                with deep domain expertise and proven delivery.
              </p>
            </div>

            <div className="flex flex-col items-center text-center" data-id="element-1333" data-mp="2ebicg69jxcvb">
              <TargetIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1334" data-mp="2e95mp6xjxy7p" />
              <h3 className="text-xl font-bold mb-3 text-nrs-navy" data-id="element-1335" data-mp="2jb38a7tfd53t">
                Mission-Critical Delivery
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1336" data-mp="2o7bo7nfg52c1">
                Trusted partner across federal agencies and enterprises with
                programs that directly support national security missions.
              </p>
            </div>

            <div className="flex flex-col items-center text-center" data-id="element-1337" data-mp="20iser4knujid">
              <FileTextIcon className="h-10 w-10 text-nrs-orange mb-4" data-id="element-1338" data-mp="2bqwe4lzwq7bs" />
              <h3 className="text-xl font-bold mb-3 text-nrs-navy" data-id="element-1339" data-mp="2bt0i13xt36c2">
                GSA MAS Contract
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1340" data-mp="2lp3js8oqx2yh">
                Multiple contract vehicles including GSA Multiple Award Schedule
                to streamline federal procurement and accelerate project starts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4: BY THE NUMBERS */}
      <section className="section-navy" data-id="element-1341" data-mp="1xzrbrc55n0xr">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1342" data-mp="22ycfvmnj24rh">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center" data-id="element-1343" data-mp="22x1718f866ti">
            <div data-id="element-1344" data-mp="27pucjyiipj6n">
              <div className="text-5xl font-bold text-nrs-orange mb-2" data-id="element-1345" data-mp="27qdpf0tb9hdt">
                <AnimatedCounter from={0} to={19} suffix="" data-id="element-1346" data-mp="1a8vnuq3tkpvs" />
              </div>
              <div className="text-white text-lg" data-id="element-1347" data-mp="1f7yhzfi9hboz">Years in Business</div>
            </div>

            <div data-id="element-1348" data-mp="3sjjgdjsj4ovy">
              <div className="text-5xl font-bold text-nrs-orange mb-2" data-id="element-1349" data-mp="3si00vvep6hv8">
                <AnimatedCounter from={0} to={95} suffix="%+" data-id="element-1350" data-mp="3ogwlfa0kanuj" />
              </div>
              <div className="text-white text-lg" data-id="element-1351" data-mp="3jicvkyiukrww">Client Retention</div>
            </div>

            <div data-id="element-1352" data-mp="3jk66wr4eo0b8">
              <div className="text-5xl font-bold text-nrs-orange mb-2" data-id="element-1353" data-mp="3ejieq24fxdff">
                <AnimatedCounter from={0} to={100} suffix="%" data-id="element-1354" data-mp="3elmimk2cacfp" />
              </div>
              <div className="text-white text-lg" data-id="element-1355" data-mp="39lgnuv0rw9te">Cleared Workforce</div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: WHAT SETS NRS APART */}
      <section className="bg-blue-50 py-20" data-id="element-1356" data-mp="30sn5uptxywbu">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-1357" data-mp="2vsqzwl1ooutn">
          <h2 className="text-3xl font-bold text-center mb-4 text-nrs-navy" data-id="element-1358" data-mp="2vulk1x54siwo">
            What Sets New River Systems Apart
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-1359" data-mp="2luurw241mfzr"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1360" data-mp="2lwxgqitgm32w">
            <div className="bg-white p-8 rounded-lg border border-gray-200 shadow-sm" data-id="element-1361" data-mp="2c5e35fsm14kl">
              <h3 className="text-xl font-bold mb-4 text-nrs-navy" data-id="element-1362" data-mp="2h6uzcy05g2uu">
                Self-Governing Development
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-1363" data-mp="238djfmghjunh">
                When we provide both governance and development services, we
                apply the same rigorous standards to our own work that we
                enforce for our clients. This eliminates the vendor conflicts
                and principal-agent problems that plague traditional consulting
                engagements where different companies handle governance versus
                implementation.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200 shadow-sm" data-id="element-1364" data-mp="3dwcgc94l1bbu">
              <h3 className="text-xl font-bold mb-4 text-nrs-navy" data-id="element-1365" data-mp="3duhson1xf542">
                Cross-Domain Expertise
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-1366" data-mp="177k5z3mroj05">
                One of the few federal contractors with cleared teams able to
                work across secure environments. We deliver ServiceNow platform
                implementations, ICAM solutions, and program management with
                proven data synchronization and security compliance patterns.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200 shadow-sm" data-id="element-1367" data-mp="o8el02i1htfk">
              <h3 className="text-xl font-bold mb-4 text-nrs-navy" data-id="element-1368" data-mp="jad5frjtoc8u">
                Complementary to ServiceNow Impact
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-1369" data-mp="y85jsgdmuulg">
                We work alongside ServiceNow Impact teams, not in competition
                with them. Impact provides product vision and adoption strategy
                while we execute the technical governance, data stewardship, and
                development. This partnership creates complete solutions for
                federal clients.
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-gray-200 shadow-sm" data-id="element-1370" data-mp="j5fh98p7l1dz">
              <h3 className="text-xl font-bold mb-4 text-nrs-navy" data-id="element-1371" data-mp="j7a1slwkirhj">
                Independent Application Rationalization
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-1372" data-mp="s6dk3nnwv2uz">
                Unlike large system integrators who profit from building
                everything on ServiceNow regardless of fit, we provide objective
                assessments of which applications should migrate to ServiceNow
                and which should remain standalone. Our track record includes
                identifying three hundred to eight hundred thousand dollars in
                cost savings through decommissioning and right-sizing
                recommendations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: CTA */}
      <section className="section-navy py-24" data-id="element-1373" data-mp="1ksgaxfaoyekt">
        <div className="max-w-3xl mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1374" data-mp="1uzr9nvrjd8fx">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white" data-id="element-1375" data-mp="1pz3eqz06s7gf">
            Ready to Discuss Your Mission-Critical Project?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed" data-id="element-1376" data-mp="1l0tism6n12tr">
            Whether you need ServiceNow governance, federal ICAM implementation,
            or program management expertise, our cleared teams are ready to
            deliver results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1377" data-mp="28r5tg4t9pdo1">
            <Link to="/contact-us" className="btn-primary" data-id="element-1378" data-mp="23yw1ykci85jn">
              Contact Business Development
            </Link>
            <Link to="/about-us/contract-vehicles" className="btn-secondary border-white text-white hover:bg-white/10 hover:text-white" data-id="element-1379" data-mp="1zwra9r5m9izn">
              View Contract Vehicles
            </Link>
          </div>
        </div>
      </section>
    </div>;
}